import { defineStore } from 'pinia';
import { base64encode } from '@/utils';

// 创建路由id
const createRouteId = (route) => {
    const { path, query = {}, params = {} } = route || {};
    if (!path) return '';
    const queryKeys = [];
    const paramKeys = [];
    const sortFn = (a, b) => {
        if (a > b) return -1;
        else if (a < b) return 1;
        return 0;
    };
    // 字段排序
    queryKeys.sort(sortFn);
    paramKeys.sort(sortFn);
    const keyValues = [];
    for (const k of queryKeys) {
        keyValues.push(`${k}=${query[k]}`);
    }
    for (const k of paramKeys) {
        keyValues.push(`${k}=${params[k]}`);
    }
    const strParams = keyValues.join('&').trim();
    let result = '';
    if (strParams.length > 0) {
        result = `${path}?${strParams}`;
    } else {
        result = path;
    }
    return base64encode(result);
};

/**
 * 创建历史路由状态存储器
 * @param storeKey
 * @param storage
 * @returns ()=>{StoreDefinition<"icloudHistoryStore", {currentGroupRoute: {}, historyGroupRoutes: {}}, {groupHistoryRoutes(*): function(*): *, groupCurrentRoute(*): function(*): *}, {add(*): void, setCurrentRoute(*, *, {}=, {}=): void, deleteOther(*): void, clear(): void, setTitle(*, *): void, delete(*): {route: *, index: number}, clearGroup(*): void}>}
 */
export const createUseHistoryStore = ({
    storeKey = 'icloud-history-store',
    storage = localStorage,
} = {}) => defineStore('icloudHistoryStore', {
    id: 'icloudHistoryStore',
    state: () => ({
        historyGroupRoutes: {}, // 当前分组历史路由
        currentGroupRoute: {}, // 当前分组路由
    }),
    getters: {
        getGroupRoutes (state) {
            return group => state.historyGroupRoutes[group];
        },
        getCurrentRoute (state) {
            return group => state.currentGroupRoute[group];
        },
        getCurrentGroupPath (state) {
            return group => {
                return state.currentGroupRoute[group] && (state.currentGroupRoute[group].path || '');
            };
        },
    },
    actions: {
        // 获取路由id
        'createRouteId' (route) {
            return createRouteId(route);
        },
        // 添加历史路由
        'add' (route) {
            const {
                name = '',
                path,
                params = {},
                query = {},
                meta: {
                    group = 'default',
                    title = '标题',
                    history = true, // 是否添加历史记录
                    closable = true,
                } = {},
            } = route || {};
            if (!path || (!history || [ '', '/', '/index', '/home' ].includes(path))) {
                this.currentGroupRoute[group] = {
                    id: 'home-page',
                    name,
                    path,
                    params,
                    query,
                    closable,
                    meta: {
                        closable,
                        title,
                        group,
                    },
                };
                return;
            }

            const routeId = createRouteId(route);
            route.id = routeId;
            this.currentGroupRoute[group] = route;
            let groupRoutes = (this.historyGroupRoutes[group] || []).filter(item => !!item.path);
            if (!groupRoutes || !Array.isArray(groupRoutes)) {
                groupRoutes = [];
            }

            const oldIdx = groupRoutes.findIndex(v => v.id === routeId);
            if (oldIdx !== -1) {
                groupRoutes[oldIdx] = {
                    id: routeId,
                    name,
                    path,
                    params,
                    query,
                    closable,
                    meta: {
                        closable,
                        title,
                        group,
                    },
                };
                this.historyGroupRoutes[group] = [ ...groupRoutes ];
                return;
            }
            // 添加历史记录
            groupRoutes.push({
                id: routeId,
                name,
                path,
                params,
                query,
                closable,
                meta: {
                    closable,
                    title,
                    group,
                },
            });
            this.$patch(state => {
                state.historyGroupRoutes[group] = groupRoutes;
                state.currentGroupRoute[group] = route;
            });
        },
        // 删除路由
        'delete' (route) {
            const {
                meta: {
                    group = 'default',
                } = {},
            } = route || {};
            const routeId = createRouteId(route);
            const groupRoutes = [ ...this.historyGroupRoutes[group] || [] ];
            let currentRoute = this.currentGroupRoute[group];
            let currentIdx = -1;
            for (const [ i, v ] of groupRoutes.entries()) {
                if (v.id === routeId) {
                    groupRoutes.splice(i, 1);
                    if (i < groupRoutes.length) {
                        currentRoute = groupRoutes[i];
                    } else {
                        if (i - 1 > 0) {
                            currentRoute = groupRoutes[i - 1];
                        } else if (groupRoutes.length > 0) {
                            currentRoute = groupRoutes[0];
                        } else {
                            currentRoute = null;
                        }
                    }
                    currentIdx = i;
                    break;
                }
            }
            this.$patch(state => {
                state.historyGroupRoutes[group] = groupRoutes;
                state.currentGroupRoute[group] = currentRoute;
            });
            return { index: currentIdx, route: currentRoute };
        },
        /**
         * 清除分组中的所有路由
         * @param group 分组名称
         * */
        'clearGroup' (group) {
            this.$patch(state => {
                state.historyGroupRoutes[group] = [];
                state.currentGroupRoute[group] = null;
            });
        },
        /**
         * 删除除了参数route外的其他视图
         * @param route 路由对象
         */
        'deleteOther' (route) {
            const {
                name = '',
                path = '',
                query = {},
                params = {},
                meta: {
                    title = '',
                    group = 'default',
                    closable = true,
                } = {},
            } = route || {};
            const routeId = createRouteId(route);
            const currentRoute = {
                id: routeId,
                name,
                path,
                params,
                query,
                meta: {
                    title,
                    closable,
                    group, // 分组
                },
            };
            this.$patch(state => {
                state.historyGroupRoutes[group] = [ currentRoute ];
                state.currentGroupRoute[group] = currentRoute;
            });
        },

        /**
         * 删除所有历史路由
         */
        'clear' () {
            this.$patch((state) => {
                state.historyGroupRoutes = {};
                state.currentGroupRoute = {};
            });
        },
        /**
         * 更改当前视图标题
         * @param route 要更改标题的路由
         * @param title
         */
        'setTitle' (route, title) {
            const routeId = createRouteId(route);
            const { meta: { group = 'default' } = {} } = route || {};
            const old = (this.historyGroupRoutes[group] || []).find(v => v.id === routeId);
            if (old) {
                if (!old.meta) {
                    old.meta = { title };
                } else {
                    old.meta.title = title;
                }
            }
        },

        /**
         * 设置为当前路由
         * @param group
         * @param path
         * @param query
         * @param params
         */
        'setCurrentRoute' (group, path, query = {}, params = {}) {
            const routeId = createRouteId({ path, query, params });
            const route = this.historyGroupRoutes[group].find(t => t.id === routeId);
            if (route) {
                this.currentGroupRoute[group] = route;
            }
        },
    },
    persist: {
        key: storeKey,
        storage: storage || localStorage,
    },
});

/**
 * 默认历史路由状态存储器
 * @returns {Store<"icloudHistoryStore", {currentGroupRoute: {}, historyGroupRoutes: {}}, {groupRoutes(*): function(*): *, groupCurrentRoute(*): function(*): *}, {add(*): void, setCurrentRoute(*, *, {}=, {}=): void, deleteOther(*): void, clear(): void, setTitle(*, *): void, delete(*): {route: *, index: number}, clearGroup(*): void}>}
 */
export const useHistoryStore = () => {
    const storeDefined = createUseHistoryStore({
        storeKey: 'icloud-history-store',
        storage: localStorage,
    });
    return storeDefined();
};
